import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { ArtikelTyp } from '../lib/api-enums';
import { ARTIKEL_TYP_LABELS as TYP_LABELS } from '../lib/api-labels';

interface Artikel {
  id: string;
  artikelnummer: string | null;
  bezeichnung: string;
  beschreibung: string | null;
  typ: ArtikelTyp;
  einheit: string;
  preisNetto: string;
  ustSatz: string;
  aktiv: boolean;
  notizen: string | null;
  createdAt: string;
  updatedAt: string;
}

export const Route = createFileRoute('/artikel/$id')({
  beforeLoad: () => requireAccess('/api/v1/artikel'),
  component: ArtikelDetailPage,
});

function ArtikelDetailPage() {
  const { id } = Route.useParams();
  const q = useQuery({
    queryKey: ['artikel', id],
    queryFn: async () => {
      try {
        return await api<{ item: Artikel }>(`/api/v1/artikel/${id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });

  if (q.isLoading) return <div className="text-slate-500">Lade…</div>;
  if (q.error) return <ErrorBanner error={q.error} />;
  if (!q.data) {
    return (
      <div className="space-y-4">
        <Link to="/artikel" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Artikelliste
        </Link>
        <div className="rounded-md border border-slate-200 bg-white p-6 text-slate-500">
          Artikel nicht gefunden.
        </div>
      </div>
    );
  }
  const a = q.data.item;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/artikel" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Artikelliste
        </Link>
      </div>
      <PageHeader
        title={a.bezeichnung}
        description={
          a.artikelnummer
            ? `Artikel-Nr. ${a.artikelnummer} · ${TYP_LABELS[a.typ]}`
            : TYP_LABELS[a.typ]
        }
      />
      <ArtikelStammdaten artikel={a} />
      <Panel title="Verwendung">
        <div className="text-sm text-slate-500">
          Angebots-/Rechnungs-Positionen und Verlauf kommen mit Phase 3 (Rechnungs-Pipeline). Hier
          wird dann sichtbar, in wie vielen Belegen dieser Artikel verwendet wurde und zu welchen
          Summen.
        </div>
      </Panel>
    </div>
  );
}

function ArtikelStammdaten({ artikel: a }: { artikel: Artikel }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    bezeichnung: a.bezeichnung,
    beschreibung: a.beschreibung ?? '',
    typ: a.typ,
    einheit: a.einheit,
    preisNetto: a.preisNetto,
    ustSatz: a.ustSatz,
    aktiv: a.aktiv,
    notizen: a.notizen ?? '',
  });
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (form.bezeichnung !== a.bezeichnung) patch['bezeichnung'] = form.bezeichnung;
      if (form.beschreibung !== (a.beschreibung ?? '')) patch['beschreibung'] = form.beschreibung;
      if (form.typ !== a.typ) patch['typ'] = form.typ;
      if (form.einheit !== a.einheit) patch['einheit'] = form.einheit;
      if (form.preisNetto !== a.preisNetto) patch['preisNetto'] = Number(form.preisNetto);
      if (form.ustSatz !== a.ustSatz) patch['ustSatz'] = Number(form.ustSatz);
      if (form.aktiv !== a.aktiv) patch['aktiv'] = form.aktiv;
      if (form.notizen !== (a.notizen ?? '')) patch['notizen'] = form.notizen;
      if (Object.keys(patch).length === 0) return a;
      return api<{ item: Artikel }>(`/api/v1/artikel/${a.id}`, { method: 'PATCH', body: patch });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['artikel', a.id] });
      void qc.invalidateQueries({ queryKey: ['artikel'] });
    },
    onError: (err) => setError(err),
  });

  return (
    <Panel
      title="Stammdaten"
      actions={
        !editing ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setForm({
                  bezeichnung: a.bezeichnung,
                  beschreibung: a.beschreibung ?? '',
                  typ: a.typ,
                  einheit: a.einheit,
                  preisNetto: a.preisNetto,
                  ustSatz: a.ustSatz,
                  aktiv: a.aktiv,
                  notizen: a.notizen ?? '',
                });
                setError(null);
                setEditing(false);
              }}
              disabled={save.isPending}
            >
              Abbrechen
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        )
      }
    >
      <ErrorBanner error={error} />
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Field label="Artikel-Nr.">
          <span className="font-mono text-slate-600">{a.artikelnummer ?? '–'}</span>
        </Field>
        <Field label="Status">
          {editing ? (
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.aktiv}
                onChange={(e) => setForm({ ...form, aktiv: e.target.checked })}
              />
              aktiv
            </label>
          ) : (
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${
                a.aktiv
                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}
            >
              {a.aktiv ? 'aktiv' : 'inaktiv'}
            </span>
          )}
        </Field>
        <Field label="Bezeichnung">
          {editing ? (
            <Input
              value={form.bezeichnung}
              onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
            />
          ) : (
            <span>{a.bezeichnung}</span>
          )}
        </Field>
        <Field label="Typ">
          {editing ? (
            <Select
              value={form.typ}
              onChange={(e) => setForm({ ...form, typ: e.target.value as ArtikelTyp })}
            >
              {(Object.keys(TYP_LABELS) as ArtikelTyp[]).map((t) => (
                <option key={t} value={t}>
                  {TYP_LABELS[t]}
                </option>
              ))}
            </Select>
          ) : (
            <span className="text-slate-600">{TYP_LABELS[a.typ]}</span>
          )}
        </Field>
        <Field label="Einheit">
          {editing ? (
            <Input
              value={form.einheit}
              onChange={(e) => setForm({ ...form, einheit: e.target.value })}
            />
          ) : (
            <span className="text-slate-600">{a.einheit}</span>
          )}
        </Field>
        <Field label="Netto-Preis">
          {editing ? (
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.preisNetto}
              onChange={(e) => setForm({ ...form, preisNetto: e.target.value })}
            />
          ) : (
            <span className="tabular-nums">
              {Number(a.preisNetto).toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              €
            </span>
          )}
        </Field>
        <Field label="USt-Satz">
          {editing ? (
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.ustSatz}
              onChange={(e) => setForm({ ...form, ustSatz: e.target.value })}
            />
          ) : (
            <span className="text-slate-600 tabular-nums">{a.ustSatz} %</span>
          )}
        </Field>
        <Field label="Brutto (berechnet)">
          <span className="text-slate-500 tabular-nums">
            {(Number(a.preisNetto) * (1 + Number(a.ustSatz) / 100)).toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            €
          </span>
        </Field>
        <div className="md:col-span-2">
          <Field label="Beschreibung">
            {editing ? (
              <textarea
                value={form.beschreibung}
                onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
                rows={3}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Wird spaeter auf Angebots-/Rechnungs-Positionen ausgegeben."
              />
            ) : (
              <span className="text-slate-600 whitespace-pre-wrap">{a.beschreibung ?? '–'}</span>
            )}
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Notizen (intern)">
            {editing ? (
              <textarea
                value={form.notizen}
                onChange={(e) => setForm({ ...form, notizen: e.target.value })}
                rows={2}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            ) : (
              <span className="text-slate-600 whitespace-pre-wrap">{a.notizen ?? '–'}</span>
            )}
          </Field>
        </div>
      </dl>
    </Panel>
  );
}

function Panel({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {actions}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
